import React, { FC, useCallback, useMemo } from 'react'
import { IColumn } from 'typescript/interfaces/tableUi'
import TableComponent, { TableComponentType } from 'UI/Table'
import { useTableComponentStyle } from 'UI/Table/style'
import { IExternalDeal } from 'typescript/interfaces/deals'
import { Box, IconButton, makeStyles, TableCell, TableFooter, TableRow, Tooltip } from '@material-ui/core'
import { ReactComponent as DeleteCircleIcon } from 'assets/svg/DeleteCircleIcon.svg'
import { ReactComponent as EditIcon } from 'assets/svg/EditIcon.svg'
import { useTableFooter } from 'components/Dashboard/style'
import classNames from 'classnames'
import { convertEmptyField, convertToPriceFormat } from 'utils/formatters'
import { getFile } from 'services/storage'
import { createLinkToDownLoad } from 'utils/downloadFile'
import { Link } from 'react-router-dom'
import { getExternalDealDocName } from './ExternalDealsForm'

const useStyles = makeStyles(() => ({
  positionControl: {
    padding: 0,
  },
}))

const TableExternalDeals = TableComponent as TableComponentType<IExternalDeal>

interface IExternalDealsTableProps {
  externalDeals: Array<IExternalDeal>
  disabled: boolean
  removeExternalDeal: (id: number) => void
  editExternalDeal: (data: {
    event: React.MouseEvent<HTMLButtonElement>
    externalDeal: IExternalDeal
  }) => void
}

const ExternalDealsTable: FC<IExternalDealsTableProps> = (props) => {
  const { externalDeals, disabled, removeExternalDeal, editExternalDeal } = props
  const classes = useTableComponentStyle()
  const styles = useStyles()

  const handleDownload = useCallback((url: string, name: string) => {
    getFile(url).then((res) => {
      createLinkToDownLoad(res.data, name)
    })
  }, [])

  const columns = useMemo<IColumn<IExternalDeal>[]>(
    () => [
      {
        label: 'Funder',
        field: 'funder',
        bodyCellClassName: classes.accentCell,
        render: (data) => convertEmptyField(data.funder),
      },
      {
        label: 'Payoff Balance',
        tooltip: 'Amount owed to external lender',
        field: 'payoffBalance',
        bodyCellClassName: classes.accentCell,
        render: (data) => convertToPriceFormat(data.payoffBalance),
      },
      {
        label: 'Wire Instructions',
        field: 'wireInstructionsUrl',
        bodyCellClassName: classes.accentCell,
        render: (data) => {
          if (!data.wireInstructionsUrl) return null
          const docName = getExternalDealDocName(data.wireInstructionsUrl) || 'wire-instructions'
          return (
            <Link
              to={data.wireInstructionsUrl}
              onClick={(e: React.MouseEvent) => {
                e.preventDefault()
                handleDownload(data.wireInstructionsUrl!, docName)
              }}
            >
              <Tooltip title={docName}>
                <Box
                  whiteSpace="nowrap"
                  maxWidth={150}
                  textOverflow="ellipsis"
                  overflow="hidden"
                >
                  {docName}
                </Box>
              </Tooltip>
            </Link>
          )
        },
      },
      {
        label: 'Payoff Letter',
        field: 'payoffLetterUrl',
        bodyCellClassName: classes.accentCell,
        render: (data) => {
          if (!data.payoffLetterUrl) return null
          const docName = getExternalDealDocName(data.payoffLetterUrl) || 'payoff-letter'
          return (
            <Link
              to={data.payoffLetterUrl}
              onClick={(e: React.MouseEvent) => {
                e.preventDefault()
                handleDownload(data.payoffLetterUrl!, docName)
              }}
            >
              <Tooltip title={docName}>
                <Box
                  whiteSpace="nowrap"
                  maxWidth={150}
                  textOverflow="ellipsis"
                  overflow="hidden"
                >
                  {docName}
                </Box>
              </Tooltip>
            </Link>
          )
        },
      },
      ...(!disabled
        ? [
            {
              label: 'Edit',
              field: 'edit',
              cellStyle: { textAlign: 'center' },
              render: (data: IExternalDeal) => (
                <IconButton
                  size="small"
                  onClick={(event) => editExternalDeal({ event, externalDeal: data })}
                  className={styles.positionControl}
                >
                  <EditIcon />
                </IconButton>
              ),
            } as IColumn<IExternalDeal>,
            {
              label: 'Delete',
              field: 'remove',
              cellStyle: { textAlign: 'center' },
              render: (data: IExternalDeal) => (
                <IconButton
                  size="small"
                  onClick={() => removeExternalDeal(data.id)}
                  className={styles.positionControl}
                >
                  <DeleteCircleIcon />
                </IconButton>
              ),
            } as IColumn<IExternalDeal>,
          ]
        : []),
    ],
    [classes, removeExternalDeal],
  )

  if (!externalDeals.length) return null

  return (
    <TableExternalDeals
      columns={columns}
      data={externalDeals}
      stickyHeader
      Footer={() => <FooterExternalDeals externalDeals={externalDeals} disabled={disabled} />}
    />
  )
}

const FooterExternalDeals: FC<{ externalDeals: Array<IExternalDeal>; disabled: boolean }> = ({ externalDeals, disabled }) => {
  const classes = useTableFooter()
  const totalPayoff = useMemo(
    () => externalDeals.reduce((sum, ed) => sum + (Number(ed.payoffBalance) || 0), 0),
    [externalDeals],
  )

  return (
    <TableFooter>
      <TableRow>
        <TableCell className={classNames(classes.cellStyle, classes.footerTableCell, classes.narrow)} />
        <TableCell className={classNames(classes.cellStyle, classes.footerTableCell, classes.narrow)}>
          {convertToPriceFormat(totalPayoff)}
        </TableCell>
        <TableCell className={classNames(classes.cellStyle, classes.footerTableCell, classes.narrow)} />
        <TableCell className={classNames(classes.cellStyle, classes.footerTableCell, classes.narrow)} />
        {!disabled && (
          <>
            <TableCell className={classNames(classes.cellStyle, classes.footerTableCell, classes.narrow)} />
            <TableCell className={classNames(classes.cellStyle, classes.footerTableCell, classes.narrow)} />
          </>
        )}
      </TableRow>
    </TableFooter>
  )
}

export default ExternalDealsTable
